import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthEndpoint } from "../../api";
import { useAuth } from "../../store/useAuth";

const FormCredentials = (
    loginMethod,
    setLoginMethod,
    loginForm,
    setLoginForm,
    onSubmit,
    error,
    isLoading
) => {
    return (
        <form
            className="ltn__form-box contact-form-box"
            onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
            }}
        >
            <div className="w-full h-10 bg-red-200 mb-20">
                Login with {loginMethod === "email" ? "Phone Number" : "Email"}?{" "}
                <span
                    onClick={() =>
                        setLoginMethod(loginMethod === "email" ? "phone_number" : "email")
                    }
                    className="underline cursor-pointer"
                >
                    Click Here
                </span>
            </div>

            {loginMethod === "email" ? (
                <input
                    type="email"
                    name="email"
                    placeholder="Email*"
                    value={loginForm.email}
                    onChange={(e) =>
                        setLoginForm((prev) => ({
                            ...prev,
                            email: e.target.value,
                            login_type: "email",
                        }))
                    }
                    required
                    disabled={isLoading}
                />
            ) : (
                <input
                    type="text"
                    name="phone_number"
                    placeholder="Phone Number*"
                    value={loginForm.phone_number}
                    onChange={(e) =>
                        setLoginForm((prev) => ({
                            ...prev,
                            phone_number: e.target.value,
                            login_type: "phone_number",
                        }))
                    }
                    required
                    disabled={isLoading}
                />
            )}

            {error && (
                <div className="text-danger mt-2 mb-2" style={{ color: "red" }}>
                    {error}
                </div>
            )}

            <div className="btn-wrapper mt-0">
                <button type="submit" className="theme-btn-1 btn btn-block" disabled={isLoading}>
                    {isLoading ? "Requesting..." : "Request OTP"}
                </button>
            </div>
        </form>
    );
};

const OtpForm = (otp, setOtp, onSubmit, error, isLoading, onResend) => {
    return (
        <form
            className="ltn__form-box contact-form-box"
            onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
            }}
        >
            <div className="mb-3">
                <h4>Enter OTP</h4>
                <p>
                    Please enter the OTP sent to your{" "}
                    {otp.login_type === "email" ? "email" : "phone number"}
                </p>
            </div>

            <input
                type="text"
                name="otp"
                placeholder="Enter OTP*"
                value={otp.otp || ""}
                onChange={(e) =>
                    setOtp((prev) => ({
                        ...prev,
                        otp: e.target.value,
                    }))
                }
                required
                disabled={isLoading}
                maxLength={6}
            />

            {error && (
                <div className="text-danger mt-2 mb-2" style={{ color: "red" }}>
                    {error}
                </div>
            )}

            <div className="btn-wrapper mt-0">
                <button type="submit" className="theme-btn-1 btn btn-block" disabled={isLoading}>
                    {isLoading ? "Verifying..." : "Verify OTP"}
                </button>
            </div>

            <div className="mt-3 text-center">
                <span
                    onClick={onResend}
                    className="underline cursor-pointer"
                    style={{ cursor: "pointer", color: "#007bff" }}
                >
                    Resend OTP
                </span>
            </div>
        </form>
    );
};

function LoginSection() {
    const navigate = useNavigate();
    const [loginMethod, setLoginMethod] = useState("email");
    const [formState, setFormState] = useState(0); // 0 = login form, 1 = OTP form
    const [loginForm, setLoginForm] = useState({
        login_type: "email",
        email: "",
        phone_number: "",
    });
    const [otpForm, setOtpForm] = useState({
        otp: "",
        login_type: "email",
        email: "",
        phone_number: "",
    });
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const authEndpoint = new AuthEndpoint();

    const handleLoginMethodChange = (newMethod) => {
        setLoginMethod(newMethod);
        setLoginForm((prev) => ({
            ...prev,
            login_type: newMethod,
            email: newMethod === "email" ? prev.email : "",
            phone_number: newMethod === "phone_number" ? prev.phone_number : "",
        }));
        setError("");
    };

    const handleRequestOtp = async () => {
        setError("");
        setIsLoading(true);

        if (loginMethod === "email" && !loginForm.email) {
            setError("Please enter your email address");
            setIsLoading(false);
            return;
        }
        if (loginMethod === "phone_number" && !loginForm.phone_number) {
            setError("Please enter your phone number");
            setIsLoading(false);
            return;
        }

        try {
            const requestData = {
                login_type: loginMethod,
                ...(loginMethod === "email"
                    ? { email: loginForm.email }
                    : { phone_number: loginForm.phone_number }),
            };
            await authEndpoint.requestOtp(requestData);

            setOtpForm({
                ...otpForm,
                login_type: loginMethod,
                email: loginForm.email,
                phone_number: loginForm.phone_number,
            });

            setFormState(1);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                    err.message ||
                    "Failed to request OTP. Please try again."
            );
        } finally {
            setIsLoading(false);
        }
    };

    const { setCurrentUser } = useAuth();

    const handleVerifyOtp = async () => {
        setError("");
        setIsLoading(true);

        if (!otpForm.otp || otpForm.otp.length < 4) {
            setError("Please enter a valid OTP");
            setIsLoading(false);
            return;
        }

        try {
            const requestData = {
                otp: otpForm.otp,
                identifier: otpForm.login_type === "email" ? otpForm.email : otpForm.phone_number,
            };

            const dataUser = await authEndpoint.login(requestData);
            console.log(dataUser);

            setCurrentUser(dataUser);

            navigate("/");
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Invalid OTP");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOtp = async () => {
        setError("");
        setIsLoading(true);

        try {
            const requestData = {
                login_type: otpForm.login_type,
                ...(otpForm.login_type === "email"
                    ? { email: otpForm.email }
                    : { phone_number: otpForm.phone_number }),
            };
            await authEndpoint.requestOtp(requestData);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                    err.message ||
                    "Failed to resend OTP. Please try again."
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="ltn__login-area pb-65">
            <div className="container">
                <div className="row">
                    <div className="col-lg-12 text-center">
                        <h1 className="section-title">
                            Sign In <br />
                            To Your Account
                        </h1>
                        <p>
                            Lorem ipsum dolor, sit amet consectetur adipisicing elit. <br />
                            Sit aliquid, Non distinctio vel iste.
                        </p>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-6">
                        <div className="account-login-inner">
                            {formState === 0
                                ? FormCredentials(
                                      loginMethod,
                                      handleLoginMethodChange,
                                      loginForm,
                                      setLoginForm,
                                      handleRequestOtp,
                                      error,
                                      isLoading
                                  )
                                : OtpForm(
                                      otpForm,
                                      setOtpForm,
                                      handleVerifyOtp,
                                      error,
                                      isLoading,
                                      handleResendOtp
                                  )}
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="account-create text-center pt-50">
                            <h4>DON'T HAVE AN ACCOUNT?</h4>
                            <p>
                                Add items to your wishlist, get personalised recommendations, <br />
                                check out more quickly, track your orders, register
                            </p>
                            <div className="btn-wrapper go-top">
                                <Link to="/register" className="theme-btn-1 btn black-btn">
                                    CREATE ACCOUNT
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LoginSection;
